import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import Card from '../common/Card';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

export const EmailVerification: React.FC = () => {
  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const { user, sendVerificationEmail, logout, error, clearError } = useAuth();

  const handleResendVerification = async () => {
    try {
      setIsResending(true);
      setResendSuccess(false);
      clearError();
      await sendVerificationEmail();
      setResendSuccess(true);
    } catch (error) {
      console.error('Failed to resend verification email:', error);
    } finally {
      setIsResending(false);
    }
  };

  const handleLogout = async () => {
    await logout();
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Verify Your Email
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Please check your email and click the verification link to continue
          </p>
        </div>

        <Card className="mt-8 space-y-6">
          <div className="space-y-4">
            <div className="flex items-center justify-center">
              <ExclamationTriangleIcon className="h-12 w-12 text-yellow-500" />
            </div>
            
            <div className="text-center">
              <p className="text-sm text-gray-700 mb-4">
                We've sent a verification email to:
              </p>
              <p className="font-medium text-gray-900 mb-6">
                {user?.email}
              </p>
              
              <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-6">
                <p className="text-sm text-blue-800">
                  <strong>Next steps:</strong>
                </p>
                <ol className="text-sm text-blue-700 mt-2 space-y-1 list-decimal list-inside">
                  <li>Check your email inbox (and spam folder)</li>
                  <li>Click the verification link in the email</li>
                  <li>Return to this page and refresh</li>
                </ol>
              </div>

              {error && (
                <div className="rounded-md bg-red-50 p-4 mb-4">
                  <div className="text-sm text-red-700">
                    <p>{error}</p>
                  </div>
                </div>
              )}

              {resendSuccess && (
                <div className="rounded-md bg-green-50 p-4 mb-4">
                  <div className="flex items-center">
                    <CheckCircleIcon className="h-5 w-5 text-green-400 mr-2" />
                    <div className="text-sm text-green-700">
                      <p>Verification email sent successfully!</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-3">
              <button
                onClick={handleResendVerification}
                disabled={isResending}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-400 disabled:cursor-not-allowed"
              >
                {isResending ? (
                  <LoadingSpinner size="small" color="white" />
                ) : (
                  'Resend Verification Email'
                )}
              </button>

              <button
                onClick={() => window.location.reload()}
                className="w-full flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                I've verified my email - Refresh
              </button>

              <button
                onClick={handleLogout}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-gray-500 bg-gray-100 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
              >
                Sign Out
              </button>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};